import {useState} from "react";
import {useMessages} from "i18n";
import {FirstMonthOfferTicket} from "upsell/FirstMonthOfferTicket";
import {FirstMonthPlansDialog} from "upsell/FirstMonthPlansDialog";
import {useCampaignCountdown} from "upsell/useCampaignCountdown";
import type {OfferMoment} from "upsell/offerEvents";
import type {FirstMonthOffer} from "upsell/firstMonthOffer";
import type {OfferFirstMonthFigures} from "upsell/firstMonthPricing";
import type {PaidPlanId} from "pricing/planCatalogue";

/**
 * The monthly campaign face. SuccessGiftFrame decides whether it owns the one post-send slot, and hands
 * in the two prices the offer states (kitecorp/transferify-ui#2545): the frame holds the storefront, so
 * it prices the offer once through `offerFirstMonthFigures` and this stays markup.
 *
 * Printed on the coupon's ticket since ui#2949 (owner 2026-09-27: it is an offer, so it gets the offer
 * look), with the remaining places back on the card beside the deadline, as the owner asked there. The
 * sentence it used to print is the ticket heading's accessible name, "from" and all, because the
 * dialog behind it still sells three tiers.
 */
export function SuccessUpsellLine({offer, plan, figures, spotsLeft, moment, occasion, endsOn, mustNotNavigate, onClaimed, onClaimPressed}: {
	offer: FirstMonthOffer;
	plan: PaidPlanId;
	/** The reduced first month and the list price for `plan`, from the storefront the reader is quoted from; stated as entry prices. */
	figures: OfferFirstMonthFigures;
	/** The api's remaining places, or null when it gave none to show. */
	spotsLeft: number | null;
	moment: OfferMoment;
	occasion: string | null;
	endsOn: string | null;
	/** An instant sender must keep their transfer tab alive during checkout. */
	mustNotNavigate: boolean;
	onClaimed: () => void;
	onClaimPressed: () => void;
}) {
	const {sender} = useMessages();
	const [picking, setPicking] = useState(false);
	const countdown = useCampaignCountdown(endsOn);
	return <>
		<FirstMonthOfferTicket testId="post-send-offer" factsTestId="post-send-offer" title={sender.upsell.prompt(figures.amount, figures.list)}
			figures={figures} plan={plan} countdown={countdown} spotsLeft={spotsLeft} actionLabel={sender.upsell.cta}
			onAction={() => {
				onClaimPressed();
				setPicking(true);
			}}/>
		<FirstMonthPlansDialog open={picking} moment={moment} preselected={plan}
			percentOff={offer.percentOff} occasion={occasion} endsOn={endsOn}
			mustNotNavigate={mustNotNavigate} onClaimed={onClaimed} onClose={() => setPicking(false)}/>
	</>;
}
